import { useContext } from 'react'
import { Button, DialogActions, DialogContent, Grid } from '@mui/material'
import { Form, Formik, FormikProps } from 'formik'
import { useSnackbar } from 'notistack'
import * as Yup from 'yup'

import DialogWithTitle from '../../../../components/dialog-with-title'
import FormikDropdown from '../../../../components/formik-dropdown'
import FormikTextField from '../../../../components/formik-textfield'
import { AppContext } from '../../../../providers/app-provider'
import Api from '../../../../utils/api'
import AccountsContext from '../../context'

interface IProps {
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  folderName?: string
  folderId: number
}

interface IFormValues {
  name: string
  accountStatus: string
}

const accountStatusOptions = [
  { id: 'Active', name: 'Active' },
  { id: 'Inactive', name: 'Inactive' },
  { id: 'Not Activated', name: 'Not Activated' },
]

const ChangeNameStatusModal = (props: IProps) => {
  const { open, setOpen, folderName, folderId } = props

  const { enqueueSnackbar } = useSnackbar()
  const { folderAttributes } = useContext(AccountsContext)
  const { host, refreshPage } = useContext(AppContext)

  const schema = Yup.object().shape({
    name: Yup.string().required('Required'),
    accountStatus: Yup.string(),
  })

  const initialValues = {
    name: folderName ?? '',
    accountStatus: folderAttributes?.accountStatus ?? '',
  }

  const handleSubmit = (values: IFormValues) => {
    const requests = []

    if (values.name !== initialValues.name) {
      requests.push(
        Api.put(`${host}/api/folders/${folderId}`, { name: values.name }).then((folderRes) => {
          if (!folderRes.ok) {
            enqueueSnackbar('Could not update name', { variant: 'error' })
          }
          return folderRes
        })
      )
    }

    if (values.accountStatus !== initialValues.accountStatus) {
      requests.push(
        Api.patch(`${host}/api/v2/folders/${folderId}/attributes`, { accountStatus: values.accountStatus }).then((attributesRes) => {
          if (!attributesRes.ok) {
            enqueueSnackbar('Could not update status', { variant: 'error' })
          }
          return attributesRes
        })
      )
    }

    Promise.all(requests).then((responses) => {
      const allSuccessful = responses.every((res) => res.ok)

      if (allSuccessful) {
        enqueueSnackbar('Successfully updated', { variant: 'success' })
        setOpen(false)
        refreshPage()
      }
    })
  }

  return (
    <DialogWithTitle setOpen={setOpen} open={open} title="Change Name or Status" maxWidth="sm" fullWidth={true}>
      <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema}>
        {({ isValid }: FormikProps<IFormValues>) => (
          <Form>
            <DialogContent>
              <Grid container={true}>
                <Grid size={12}>
                  <FormikTextField name="name" label="Account Name" required={true} className="fs-exclude" />
                </Grid>

                <Grid size={6}>
                  <FormikDropdown name="accountStatus" label="Status" items={accountStatusOptions} required={false} size="medium" />
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button variant="outlined" onClick={() => setOpen(false)}>
                Cancel
              </Button>

              <Button variant="contained" type="submit" disabled={!isValid}>
                Apply
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </DialogWithTitle>
  )
}

export default ChangeNameStatusModal
